import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, Link as LinkIcon, Loader2, Video as VideoIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export function VideoUpload({ onVideoSelected, currentVideo, folderPath }) {
  const [uploadMethod, setUploadMethod] = useState('upload');
  const [videoUrl, setVideoUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(currentVideo || null);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please upload a video file');
      return;
    }

    // Validate file size (max 500MB for videos)
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Video size must be less than 500MB');
      return;
    }

    setIsUploading(true);

    try {
      // Generate a unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${timestamp}-${Math.random().toString(36).substring(7)}.${fileExtension}`;

      // Get signed upload URL from edge function
      const { data: urlData, error: urlError } = await supabase.functions.invoke('generate-upload-url', {
        body: {
          action: 'upload',
          fileName: fileName,
          contentType: file.type,
          folderPath: folderPath,
        },
      });

      if (urlError) throw urlError;

      // Upload file to GCS using signed URL
      const uploadResponse = await fetch(urlData.signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      // Set the preview and notify parent
      setPreview(urlData.publicUrl);
      onVideoSelected(urlData.publicUrl);
      toast.success('Video uploaded successfully');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (!videoUrl) {
      toast.error('Please enter a video URL');
      return;
    }

    const trimmed = videoUrl.trim();
    const isVariable = /\{\{.*?\}\}/.test(trimmed);

    if (!isVariable) {
      try {
        new URL(trimmed);
      } catch {
        toast.error('Please enter a valid URL or {{ variable }}');
        return;
      }
    }

    setPreview(isVariable ? null : trimmed);
    onVideoSelected(trimmed);
    toast.success(isVariable ? 'Variable URL added' : 'Video URL added');
  };

  // Check if URL is a YouTube or Vimeo embed
  const isEmbedUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com/embed') ||
           url.includes('youtu.be') ||
           url.includes('vimeo.com');
  };

  return (
    <div className="space-y-4">
      <Tabs value={uploadMethod} onValueChange={setUploadMethod}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload">Upload</TabsTrigger>
          <TabsTrigger value="url">Video URL</TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-muted-foreground/50 transition-colors">
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">Upload Video</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Choose a video file from your computer
            </p>
            <label htmlFor="video-upload">
              <Button asChild disabled={isUploading}>
                <span>
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Choose File'
                  )}
                </span>
              </Button>
              <input
                id="video-upload"
                type="file"
                accept="video/*"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          </div>
          <div className="text-xs text-muted-foreground text-center">
            Supported formats: MP4, WebM, MOV, AVI (Max 500MB)
          </div>
        </TabsContent>

        {/* URL Tab */}
        <TabsContent value="url" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="video-url">Video URL</Label>
            <div className="flex gap-2">
              <Input
                id="video-url"
                placeholder="https://youtube.com/embed/... or {{ variable }}"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUrlSubmit();
                  }
                }}
              />
              <Button onClick={handleUrlSubmit}>
                <LinkIcon className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Supports YouTube embeds, Vimeo, and direct video URLs
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview */}
      {preview && (
        <div className="space-y-2">
          <Label>Preview</Label>
          <div className="border rounded-lg overflow-hidden bg-black">
            {isEmbedUrl(preview) ? (
              // YouTube/Vimeo embed
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={preview}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Video preview"
                />
              </div>
            ) : (
              // Direct video file
              <video
                src={preview}
                controls
                className="w-full h-auto max-h-64"
                onError={() => {
                  toast.error('Failed to load video preview');
                  setPreview(null);
                }}
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
